import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import pluralize from 'pluralize';

import { DATE_FORMAT_WITH_TIME } from '../../../../constants/dates';
import getConfig from '../../../../util/config';
import { getThumbBySize } from '../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import { prependAccountSlug } from '../../../../util/account';
import WebGalleryThumbnailActions from '../WebGalleryThumbnailActions';
import { ALBUM_PRIVACY, ALBUM_TYPE } from '../../../../constants/albums';
import ListingCardTemplate from '../../../pro/components/ListingCardTemplate';
import {
  isMemoryBook,
  isVGBContext,
  isVideoAlbum,
} from '../../../../util/album';
import WebGalleryVideoGuestbookListingCard from './components/WebGalleryVideoGuestbookListingCard';
import WebGalleryGroupVideoListingCard from './components/WebGalleryVideoGroupListingCard';
import WebGalleryMemoryBookListingCard from './components/WebGalleryMemoryBookListingCard';
import WebGalleryEventListingCard from './components/WebGalleryEventListingCard';
import { getAsset } from '../../../../util/assets';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    last_uploaded_at: PropTypes.string,
  }).isRequired,
  eventSettings: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }).isRequired,
};

const WebGalleryAlbumCard = ({ album, eventSettings }) => {
  if (!album) {
    return null;
  }

  const config = getConfig();

  const thumbs = album.cover_photo?.thumbnail_urls ?? [];

  const thumb = getThumbBySize(
    thumbs,
    IMAGE_SIZE_CODE.MEDIUM2X,
    // Important for when using local cache
    true,
  );

  const thumbnailUrl = thumb?.url;

  const lastUploadedAt = album.last_uploaded_at;
  const totalPhotos = album?.photos?.total ?? 0;
  const linkTo = prependAccountSlug(`/galleries/view/album/${album.id}`, album);

  if (isVGBContext(album) && isVideoAlbum(album)) {
    return (
      <WebGalleryVideoGuestbookListingCard
        album={album}
        linkTo={linkTo}
        thumbnailActions={
          album.album_type === ALBUM_TYPE.EVENT ? undefined : (
            <WebGalleryThumbnailActions
              albumId={album.id}
              url={`${config.app.baseUrl}${linkTo}`}
              quote="Check out this awesome album!"
              entity={album}
            />
          )
        }
        thumbnails={thumbs}
        eventSettings={eventSettings}
      />
    );
  }

  if (isVideoAlbum(album)) {
    return (
      <WebGalleryGroupVideoListingCard
        album={album}
        linkTo={linkTo}
        thumbnailActions={
          album.album_type === ALBUM_TYPE.EVENT ? undefined : (
            <WebGalleryThumbnailActions
              albumId={album.id}
              url={`${config.app.baseUrl}${linkTo}`}
              quote="Check out this awesome album!"
              entity={album}
              eventSettings={eventSettings}
            />
          )
        }
        thumbnails={thumbs}
      />
    );
  }

  if (isMemoryBook(album)) {
    return (
      <WebGalleryMemoryBookListingCard
        album={album}
        linkTo={linkTo}
        thumbnails={thumbs}
        eventSettings={eventSettings}
      />
    );
  }

  if (album?.album_type === ALBUM_TYPE.EVENT) {
    return (
      <WebGalleryEventListingCard
        album={album}
        linkTo={linkTo}
        thumbnails={thumbs}
        eventSettings={eventSettings}
      />
    );
  }

  return (
    <ListingCardTemplate
      thumbnailUrl={thumbnailUrl}
      linkTo={linkTo}
      title={album.name}
      templateLinkProps={{
        rel: album?.privacy === ALBUM_PRIVACY.PRIVATE ? 'nofollow' : undefined,
      }}
      subtitle={
        totalPhotos > 0
          ? `${totalPhotos} ${pluralize('Photo', totalPhotos)}`
          : undefined
      }
      note={
        lastUploadedAt ? (
          <span className="text-center">
            Last Updated: {moment(lastUploadedAt).format(DATE_FORMAT_WITH_TIME)}
          </span>
        ) : undefined
      }
      nullState={
        <>
          <div className="listing-card-template-cover-null-state">
            <img
              src={getAsset('/waldo-icon.svg')}
              alt=""
              className="album-folders-null-state-icon"
            />
            <p>No photos yet</p>
          </div>
        </>
      }
      thumbnailActions={
        <WebGalleryThumbnailActions
          albumId={album.id}
          url={`${config.app.baseUrl}${linkTo}`}
          quote="Check out this awesome album!"
          entity={album}
        />
      }
    />
  );
};

WebGalleryAlbumCard.propTypes = propTypes;

export default WebGalleryAlbumCard;
